import { isTerminalStatus, type JobType } from '@detachly/contracts'
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArtifactRow } from '../components/ArtifactRow'
import { InventoryDetail } from '../components/InventoryDetail'
import {
  Button,
  Card,
  CodeInline,
  EmptyState,
  ErrorPanel,
  Metric,
  Skeleton,
  StatusBadge,
} from '../components/ui'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useNow } from '../hooks/useNow'
import { useResource } from '../hooks/useResource'
import { useApi } from '../lib/api-context'
import { formatDateTime, formatElapsed, jobLabel, maskedKey } from '../lib/format'

/** Roughly half a minute of 2s polls: long enough for a webhook, short enough not to nag. */
const PURCHASE_WAIT_TICKS = 15

export function ProjectHubPage() {
  const { projectId = '' } = useParams()
  const api = useApi()
  const navigate = useNavigate()

  const project = useResource(() => api.getProject(projectId), [api, projectId])
  const inventory = useResource(() => api.getInventory(projectId), [api, projectId])
  const jobs = useResource(() => api.listJobs(projectId), [api, projectId])
  const packages = useResource(() => api.listEntitlements(projectId), [api, projectId])

  const [starting, setStarting] = useState<JobType | null>(null)
  const [startError, setStartError] = useState<unknown>(null)

  const start = async (type: JobType, options?: { retryOfJobId?: string }) => {
    setStarting(type)
    setStartError(null)
    try {
      const job = options
        ? await api.startJob(projectId, type, options)
        : await api.startJob(projectId, type)
      navigate(`/app/jobs/${job.jobId}`)
    } catch (error: unknown) {
      setStartError(error)
      setStarting(null)
    }
  }

  const scan = inventory.data?.scan ?? null
  const scanned = scan !== null
  const items = jobs.data?.items ?? []
  const aiPages = packages.data?.aiPagesRemaining ?? 0
  const retries = packages.data?.retriesRemaining ?? 0
  const lastAiJob = items.find((job) => job.type === 'ai_home' && isTerminalStatus(job.status))
  const canRetryAi = retries > 0 && lastAiJob !== undefined
  const canStartAi = aiPages > 0 || canRetryAi

  // Polar sends the buyer back here; the entitlement arrives moments later on its own webhook.
  const [query] = useSearchParams()
  const justPaid = query.get('purchase') === 'success'
  const [waitedFor, setWaited] = useState(0)
  const awaitingPackage = justPaid && aiPages === 0 && packages.error === null
  useAutoRefresh(
    awaitingPackage && waitedFor < PURCHASE_WAIT_TICKS,
    () => {
      setWaited((ticks) => ticks + 1)
      packages.reload()
    },
    { busy: packages.refreshing },
  )

  // A scan started here or in another tab keeps the metrics below it moving until it lands (F17).
  // A failing endpoint stops the loop: polling a broken API every two seconds helps nobody.
  const scanning = items.some((job) => job.type === 'scan' && !isTerminalStatus(job.status))
  const liveJob = items.find((job) => !isTerminalStatus(job.status)) ?? null
  const now = useNow(liveJob !== null && liveJob.type !== 'scan')
  useAutoRefresh(
    scanning && jobs.error === null && inventory.error === null,
    () => {
      jobs.reload()
      inventory.reload()
    },
    { busy: jobs.refreshing || inventory.refreshing },
  )
  // AI / scaffold / pack do not change inventory, but the hub still has to look alive if the
  // customer left the job page.
  useAutoRefresh(liveJob !== null && !scanning && jobs.error === null, () => jobs.reload(), {
    busy: jobs.refreshing,
  })

  // A download built before the last scan is not wrong, it is just older than what is on screen.
  const artifacts = items.flatMap((job) =>
    job.artifacts.map((artifact) => ({
      artifact,
      job,
      stale: scan !== null && job.scanSnapshotId !== null && job.scanSnapshotId !== scan.id,
    })),
  )
  const staleCount = artifacts.filter((entry) => entry.stale).length

  if (project.loading) return <Skeleton rows={4} />
  if (project.error) return <ErrorPanel error={project.error} onRetry={project.reload} />
  if (!project.data) return null

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>{project.data.name}</h1>
          <p className="muted">
            <CodeInline>{project.data.framerProjectKey ?? project.data.framerUrl}</CodeInline> · key{' '}
            <CodeInline>{maskedKey(project.data.apiKeyLast4)}</CodeInline>
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void start('scan')}
          disabled={starting !== null || scanning}
        >
          {starting === 'scan'
            ? 'Starting…'
            : scanning
              ? 'Scanning…'
              : scanned
                ? 'Re-scan'
                : 'Scan now'}
        </Button>
      </div>

      {startError ? <ErrorPanel error={startError} /> : null}

      {justPaid ? (
        <div className="notice" role="status">
          <p className="notice__title">
            {aiPages > 0
              ? `Payment received — this project has ${aiPages} AI page${aiPages === 1 ? '' : 's'}.`
              : 'Payment received.'}
          </p>
          <p>
            {aiPages > 0
              ? 'You can start the AI rebuild below; the pages stay on the project for 12 months.'
              : waitedFor < PURCHASE_WAIT_TICKS
                ? 'Waiting for the payment provider to confirm it — this page updates by itself.'
                : 'The payment provider has not confirmed it yet. Reload in a minute; nothing is lost if it takes longer.'}
          </p>
        </div>
      ) : null}

      <div className="stack">
        <div className="metrics">
          <Metric label="Pages" value={scan ? scan.pageCount : '—'} />
          <Metric label="Collections" value={scan ? scan.collectionCount : '—'} />
          <Metric
            label="CMS items"
            value={scan ? scan.collections.reduce((total, c) => total + c.itemCount, 0) : '—'}
          />
          <Metric
            label="Last scan"
            value={scanning ? 'Scanning…' : scan ? formatDateTime(scan.scannedAt) : 'Never'}
          />
        </div>

        {inventory.error ? <ErrorPanel error={inventory.error} onRetry={inventory.reload} /> : null}

        {scanning ? (
          <p className="subtle" role="status">
            A scan is running. These numbers are from the previous one and will update by
            themselves.
          </p>
        ) : null}

        {liveJob && liveJob.type === 'ai_home' ? (
          <div className="notice" role="status">
            <p className="notice__title">
              AI rebuild still running · {formatElapsed(liveJob.createdAt, now)}
            </p>
            <p>This usually takes 5–20 minutes. Safe to leave — we email you when it finishes.</p>
            <div>
              <Link to={`/app/jobs/${liveJob.id}`} className="button button--secondary">
                Open the job
              </Link>
            </div>
          </div>
        ) : null}

        {liveJob && liveJob.type !== 'scan' && liveJob.type !== 'ai_home' ? (
          <p className="subtle" role="status">
            {jobLabel(liveJob.type)} still running · {formatElapsed(liveJob.createdAt, now)}.{' '}
            <Link to={`/app/jobs/${liveJob.id}`}>Open the job</Link>
          </p>
        ) : null}

        {staleCount > 0 && !scanning ? (
          <div className="notice notice--warning">
            <p className="notice__title">
              {staleCount === 1
                ? 'One download was built before the last scan.'
                : `${staleCount} downloads were built before the last scan.`}
            </p>
            <p>Build them again to pick up what changed in Framer.</p>
          </div>
        ) : null}

        {scan && scan.warnings.length > 0 ? (
          <div className="notice notice--warning">
            <p className="notice__title">
              Framer answered only part of this project ({scan.warnings.length}
              {scan.warnings.length === 1 ? ' warning' : ' warnings'}).
            </p>
            <ul className="help-list">
              {scan.warnings.slice(0, 4).map((warning, index) => (
                <li key={index}>
                  {warning.scope}: {warning.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!scanned && !inventory.loading ? (
          <EmptyState
            title="Nothing scanned yet"
            description="Run a scan to see the pages, collections and styles Detachly can export. It only reads your project."
            action={
              <Button onClick={() => void start('scan')} disabled={starting !== null}>
                Scan this project
              </Button>
            }
          />
        ) : null}

        <div className="grid">
          <Card>
            <div className="card__title">
              <h2>Content Pack</h2>
              <span className="badge">Free</span>
            </div>
            <p className="muted">
              Every collection as JSON and CSV, design tokens, asset URLs and a README — zipped.
              Built from the last scan, so it can only contain what your project really has.
            </p>
            <div className="row" style={{ marginTop: 16 }}>
              <Button
                onClick={() => void start('content_pack')}
                disabled={!scanned || scanning || starting !== null}
              >
                {starting === 'content_pack' ? 'Starting…' : 'Build Content Pack'}
              </Button>
              {!scanned ? <span className="subtle">Run a scan first</span> : null}
              {scanned && scanning ? (
                <span className="subtle">Waiting for the scan to finish</span>
              ) : null}
            </div>
          </Card>

          <Card>
            <div className="card__title">
              <h2>Scaffold</h2>
              <span className="badge">Free</span>
            </div>
            <p className="muted">
              A Vite + React project with your routes, CMS content and tokens as CSS variables. It
              is installed and built before you can download it — page design is left to you.
            </p>
            <div className="row" style={{ marginTop: 16 }}>
              <Button
                onClick={() => void start('scaffold')}
                disabled={!scanned || scanning || starting !== null}
              >
                {starting === 'scaffold' ? 'Starting…' : 'Build Scaffold'}
              </Button>
              {!scanned ? <span className="subtle">Run a scan first</span> : null}
              {scanned && scanning ? (
                <span className="subtle">Waiting for the scan to finish</span>
              ) : null}
            </div>
          </Card>

          <Card>
            <div className="card__title">
              <h2>AI rebuild</h2>
              <span className="badge">
                {aiPages > 0
                  ? `${aiPages} AI page${aiPages === 1 ? '' : 's'} ready`
                  : canRetryAi
                    ? `${retries} retr${retries === 1 ? 'y' : 'ies'} ready`
                    : 'Needs a package'}
              </span>
            </div>
            <p className="muted">
              Assisted pure React for your Home page. Motion may be simplified, and every run ships
              a report saying where it differs. It only starts once the page is installed and built
              here — a rebuild that does not compile costs you nothing. A Home rebuild usually
              takes 5–20 minutes; you can close this tab — we email you when it finishes.
            </p>
            <div className="row" style={{ marginTop: 16 }}>
              <Button
                onClick={() =>
                  void start(
                    'ai_home',
                    aiPages === 0 && lastAiJob ? { retryOfJobId: lastAiJob.id } : undefined,
                  )
                }
                disabled={!scanned || scanning || starting !== null || !canStartAi}
              >
                {starting === 'ai_home'
                  ? 'Starting…'
                  : aiPages === 0 && canRetryAi
                    ? 'Retry Home rebuild'
                    : 'Rebuild Home with AI'}
              </Button>
              {scanned && aiPages === 0 && canRetryAi ? (
                <span className="subtle">Uses one retry from your package</span>
              ) : null}
              {scanned && aiPages === 0 && !canRetryAi ? (
                <span className="subtle">Choose a package to unlock it</span>
              ) : null}
              {scanned ? (
                <Link
                  to={`/app/projects/${projectId}/packages`}
                  className="button button--secondary"
                >
                  {aiPages > 0 ? 'See packages' : 'Choose a package'}
                </Link>
              ) : (
                <span className="subtle">Run a scan first</span>
              )}
            </div>
          </Card>
        </div>

        {scan ? <InventoryDetail scan={scan} /> : null}

        <Card flat>
          <div className="card__title">
            <h2>Downloads</h2>
          </div>
          {jobs.loading ? <Skeleton rows={2} /> : null}
          {jobs.error ? <ErrorPanel error={jobs.error} onRetry={jobs.reload} /> : null}
          {jobs.data && artifacts.length === 0 ? (
            <p className="muted">
              Nothing to download yet. Finished jobs put their files here for 30 days.
            </p>
          ) : null}
          {artifacts.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">File</th>
                  <th scope="col">From</th>
                  <th scope="col">Size</th>
                  <th scope="col">Available</th>
                  <th scope="col" className="table__actions">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {artifacts.map(({ artifact, job, stale }) => (
                  <ArtifactRow key={artifact.id} artifact={artifact} job={job} stale={stale} />
                ))}
              </tbody>
            </table>
          ) : null}
        </Card>

        <Card flat>
          <div className="card__title">
            <h2>Recent jobs</h2>
          </div>
          {jobs.data && jobs.data.items.length === 0 ? (
            <p className="muted">No jobs have run for this project yet.</p>
          ) : null}
          {jobs.data && jobs.data.items.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Job</th>
                  <th scope="col">Status</th>
                  <th scope="col">Started</th>
                  <th scope="col" className="table__actions">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {jobs.data.items.map((job) => (
                  <tr key={job.id}>
                    <td>{jobLabel(job.type)}</td>
                    <td>
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="muted">{formatDateTime(job.createdAt)}</td>
                    <td className="table__actions">
                      <Link to={`/app/jobs/${job.id}`} className="button button--secondary">
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </Card>
      </div>
    </>
  )
}
